
    const data = [
  {
    "title": "Principles",
    "id": "principles",
    "elements": [
      {
        "type": "paragraph",
        "text": "Early discovery teams organize their data by adopting conventions for things like how ids are shared between systems, when data gets moved from one system to another and how files and folders are named. Each team decides how consistent to make the conventions across users and teams, and how deliberately to define their conventions vs. allowing them to emerge naturally."
      },
      {
        "type": "paragraph",
        "text": "For teams that want to deliberately design consistent conventions, this framework provides a way for thinking about them, based around three principles:"
      },
      {
        "type": "heading",
        "size": 2,
        "title": "1. Make it easier to follow conventions than to not follow them"
      },
      {
        "type": "paragraph",
        "text": "Drug discovery teams are always busy even during quite periods. When things inevitably get crazier than usual, the last thing they pay attention to is following detailed rules that may or may not help someone else find their data in six months. So your data conventions should be simple enough that they can quickly turn into habits that the team will continue to follow even when it's the last thing on their mind."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "2. Maximize flexibility for decision making"
      },
      {
        "type": "paragraph",
        "text": "The payoff for maintaining consistent data conventions is being able to combine data from different assays and experiments into a form that enables clear decision making. However, it's impossible to predict in advance exactly what that forms of the final data you'll need. So the best strategy is to organize the data in a way that allows you to figure that out at the end."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "3. Maximize consistency as allowed by the maturity level of the science"
      },
      {
        "type": "paragraph",
        "text": "The key phrase in this one is \"the maturity level of the science\". Most assays progress through stages where they become increasingly consistent. The rules for how the resulting data is processed needs to progress as well, starting from highly flexible guidelines when the assays is chaotically evolving to more detailed rules once as it settles down."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Outline"
      },
      {
        "type": "paragraph",
        "text": "The rest of this framework is organized as follows:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Making Decisions: Outlines what the data needed for decision making commonly looks like and how to make it as flexible as possible."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Processing Datasets: Describes the different ways data gets from its source to its final form and what the different levels of maturity look like."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Data Categories: Provides a framework for defining data categories that allow teams to discuss and identify the right requirements for each dataset they work with."
        ]
      }
    ]
  },
  {
    "title": "The Feature Matrix",
    "id": "features",
    "elements": [
      {
        "type": "paragraph",
        "text": "The central decision-making process for a typical drug discovery program is looking at a table of values that have been measured, calculated, predicted or inferred about different drug candidates and deciding which of them should move on to the next round of studies/experiments/trials/etc."
      },
      {
        "type": "paragraph",
        "text": "Teams typically need different tables, or different versions of the same tables at different stages in the process. So the goal should be to be able to quickly, flexibly and reliably generate these tables, rather than to try and anticipate and generate exactly what they need. The rest of this page will define some core concepts that will help you think about how to do this."
      },
      {
        "type": "paragraph",
        "text": "You don't need to use these same terms or even these exact definitions, but this should help you think about how to frame the problem for your own team. Notes on implementation are in a later section."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Candidates and Features"
      },
      {
        "type": "paragraph",
        "text": "First, we need a generic term for the things we're making decisions about."
      },
      {
        "type": "bullets",
        "bullets": [
          "Candidate: A (potential) drug candidate such as a small molecule structure, protein sequence, etc. or more generally anything for which you're generating measurements, predictions, etc."
        ]
      },
      {
        "type": "paragraph",
        "text": "Next, we need terms for the values that are measured, calculated, predicted or inferred about different candidates. We'll borrow a term from machine learning:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Feature: A particular type of measurement, prediction or inferred value that can be calculated for a candidate."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Feature value: The value of a particular feature, calculated for a particular candidate."
        ]
      },
      {
        "type": "paragraph",
        "text": "So a feature is a class of feature values. Depending on the context, two feature values generated at different times may be considered to be for the same feature or different features. For example, two readout values generated from different experiments with very similar protocols might be considered comparable (and thus the same feature) for some decisions but not for others. This is a complexity that will come up throughout this framework."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The Feature Matrix"
      },
      {
        "type": "paragraph",
        "text": "We can think of feature values as being organized into a giant sparse matrix:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Feature Matrix: The collection of all features for all candidates, thought of as a matrix in which rows represent candidates and columns represent features."
        ]
      },
      {
        "type": "paragraph",
        "text": "Of course, this idea of arranging feature values is not completely accurate. As noted above, feature values may be considered the same or different features depending on context. Conversely, a single asset will often have different values for the same feature, collected from different experiments, or replicates within the same experiment."
      },
      {
        "type": "paragraph",
        "text": "So this isn't a real matrix, but it's often useful to pretend that it is. In practice, you'll only need to look at subsets of the matrix at any given time. In fact, there are two types of subsets that you'll mostly need to care about."
      },
      {
        "type": "paragraph",
        "text": "The first is a subset of the matrix that comes from a single experiment/run/etc.:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Result set: A collection of feature values for multiple candidates, and potentially multiple features, calculated at the same time, for example from a single experiment."
        ]
      },
      {
        "type": "paragraph",
        "text": "Note that you don't necessarily need to store result sets as tables with feature columns. Depending on the type data, there may be better ways to structure the files. You just need to be able to extract specified feature values to build the decision making tables described above."
      },
      {
        "type": "bullets",
        "bullets": [
          "Slice: A sub-matrix of the feature matrix defined by a set of candidates and a set of features, collected by comparing equivalent features across different result sets."
        ]
      },
      {
        "type": "paragraph",
        "text": "To restate what was discussed at the top of this page in the new terminology, the goal of all of this is for users to be able to create slices of the feature matrix as quickly, flexibly and reliably as possible. To understand how to do that, we need to look at the processes that turn raw data into results sets. The next page introduces this terminology."
      }
    ]
  },
  {
    "title": "Data Lineage",
    "id": "datasets",
    "elements": [
      {
        "type": "paragraph",
        "text": "The data that makes it into each result set typically goes through a series of processing and analysis steps. So we'll need some standardized terms for the steps and the artifacts of this process."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Datasets"
      },
      {
        "type": "bullets",
        "bullets": [
          "Dataset: A static collection of related data, often generated from one or more other datasets."
        ]
      },
      {
        "type": "paragraph",
        "text": "A result set is one type of dataset, but so is each intermediate form of the data on its way to becoming a result set."
      },
      {
        "type": "paragraph",
        "text": "Note that we're specifying that every dataset is static, i.e. once it's created it doesn't change. In practice, the term is often used more generally to include collections of data that evolve over time, either by adding records or updating existing records. But we'll use the other terms like data sources or data collections for those cases."
      },
      {
        "type": "paragraph",
        "text": "In early stage drug discovery, most data comes in batches from individual experiments, then goes through discrete processing steps, each producing a static dataset. In cases where data needs to evolve, you can usually deal with this by taking snapshots, each of which becomes its own dataset."
      },
      {
        "type": "paragraph",
        "text": "This assumption makes the rest of the framework significantly simpler, so that's what we're going to do."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Lineage"
      },
      {
        "type": "bullets",
        "bullets": [
          "Processing step: The process that generates a new dataset from other datasets or other sources of data (including non-static sources)."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Output dataset: The dataset produced by a processing step."
        ]
      },
      {
        "type": "paragraph",
        "text": "A processing step may involve deterministic algorithms, predictive models, human judgement or any other combination of processes. The key is that each processing step generates exactly one output dataset, though it may use multiple datasets to do this."
      },
      {
        "type": "bullets",
        "bullets": [
          "Input dataset: A dataset that is used by processing step to produce the output dataset."
        ]
      },
      {
        "type": "paragraph",
        "text": "If you have a sequence of processing steps where the output of each step is the input, or the primary input, for the next step, you can view these datasets as being different versions of the same underlying data after different processing steps. We treat these different versions as distinct datasets, but we need a concept for all of them together."
      },
      {
        "type": "bullets",
        "bullets": [
          "Data lineage: The collection of input datasets and processing that generated a particular dataset, traced back transitively to the original sources."
        ]
      },
      {
        "type": "paragraph",
        "text": "We'll particularly care about data lineages of result sets, but the concept can be applied to any dataset."
      }
    ]
  },
  {
    "title": "Data Categories",
    "id": "categories",
    "elements": [
      {
        "type": "paragraph",
        "text": "To be able to compare features across different result sets, we need to ensure that their data lineages involve consistent processing steps, starting from consistent data sources. But first we need to decide which datasets and lineages we want to be comparable and consistent."
      },
      {
        "type": "bullets",
        "bullets": [
          "Data Category: A class of data lineages that start from comparable/equivalent data sources and follow equivalent processing steps through datasets with the same or comparable data structures."
        ]
      },
      {
        "type": "paragraph",
        "text": "This definition is intentionally flexible and vague because it's impossible to define clear, exhaustive and robust data categories without creating an unrealistically rigid structure. So defining data categories is more of an art than a science. Here are some things to keep in mind for those who try:"
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Caveats"
      },
      {
        "type": "bullets",
        "bullets": [
          "Vibes over implementation: Data Categories should be defined by how the team thinks about the data rather than how it's actually processed and analyzed."
        ]
      },
      {
        "type": "paragraph",
        "text": "The way that an assay is processed and analyzed will evolve over time, at both superficial and more fundamental levels. You need to capture those differences in ways that will allow you to interpret features and define consistent slices, but most of those changes shouldn't warrant a new data category."
      },
      {
        "type": "paragraph",
        "text": "Data categories are more a starting point for the team to agree on how to think about different datasets and lineages. So the most important thing is to keep them compatible with how the team wants to think about their data."
      },
      {
        "type": "bullets",
        "bullets": [
          "Destinations over sources: Data Categories should generally be defined by how the (types of) features in the result set rather than the type of assay or instrument the data came from."
        ]
      },
      {
        "type": "paragraph",
        "text": "It's tempting to define data categories by the type of source data. However, data from the same instrument or even the same assay can be used in very different ways depending on how the experiment was set up and what its goals were."
      },
      {
        "type": "paragraph",
        "text": "Data categories are defined by the lineage of datasets, which is usually dictated by the features being calculated more than by the source data."
      },
      {
        "type": "bullets",
        "bullets": [
          "Catch-all Categories: It's good to have one or more catch-all categories for datasets/lineages that don't fit anywhere else."
        ]
      },
      {
        "type": "paragraph",
        "text": "The most important thing about data categories is that they're simple and natural enough for your team to define, maintain and use them. It usually isn't worth the trouble to carefully define categories for all of the long-tail assays that get run a few times a month, and definitely not for on-offs."
      },
      {
        "type": "paragraph",
        "text": "So start by defining categories that will allow your team to start working with them, even if some don't strictly match the definition above. An OK system that your team uses is infinitely better than a perfect system that they don't."
      }
    ]
  },
  {
    "title": "Maturity Levels",
    "id": "maturity",
    "elements": [
      {
        "type": "paragraph",
        "text": "Different data categories will have different technical requirements, particularly when it comes to the consistency of datasets across different data lineages. Moreover, these requirements can change over time depending on a number of factors."
      },
      {
        "type": "paragraph",
        "text": "In particular, data categories tend to become more consistent as they become more mature, allowing more strict and detailed requirements. So to make sure the team is on the same page about these requirements, it's useful to think of each category on a common spectrum of maturity levels."
      },
      {
        "type": "paragraph",
        "text": "Every team/organization should define its own maturity levels based on their unique needs, but this framework will identify some factors to consider and provide a starting point."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Factors"
      },
      {
        "type": "paragraph",
        "text": "Three factors tend to be most important in defining maturity levels:"
      },
      {
        "type": "bullets",
        "bullets": [
          "1. Source Consistency: Assays and other data sources evolve different rates throughout their life cycle. The consistency of the source data is the primary factor that defines how consistent the rest of the lineage can be."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "2. Frequency: For a processing step that you run once a quarter, it usually isn't worth the time to try and nail it down. One the other hand, if you run it multiple times a day, it had better be more or less automated. In between, there's a spectrum of how consistent you need a lineage to be."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "3. Importance: The more a results set contributes to more important, expensive decisions, the more you need the lineage that produces it to be consistent and reproducible."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Starting Levels"
      },
      {
        "type": "paragraph",
        "text": "Here are some levels you can use as a starting point to define your own maturity levels:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Throw-away: Data that you are absolutely sure you will look at once, then throw away. Basically no consistency requirements. Just make sure this doesn't become the default. It's easy to convince yourself you won't need to look at the data again, then discover you were wrong."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Exploratory: Assays that are effectively one-off. You may run similar assays again, but with the protocol modified enough that it isn't worth trying to make them consistent. However, you may use the data again, or need to verify/reproduce it. So you need a detailed record (including metadata) but there's not consistency requirement."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Infrequent: The same assay run repeatedly but infrequently, and with enough variation between runs that there isn't a consistent structure/schema. You need enough metadata to be able to compare runs to each other, but it will probably be a more bespoke process."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Frequent: The same assay is run fairly regularly and has matured to the point where the data can be fairly consistent. However, there may still be minor adjustments between runs, and most of the processing steps are run manually. This is often a transitional state between infrequent and automated."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Automated: The same assays is run frequently and consistently enough that the team has invested in automating most or all of the processing steps that define the data lineages. They may also have automated the lab processes that generate the data."
        ]
      }
    ]
  },
  {
    "title": "Implementation Notes",
    "id": "implementation",
    "elements": [
      {
        "type": "paragraph",
        "text": "The previous sections of this framework defined the basic concepts that teams can use to think about their data. However, the underlying implementation doesn't necessarily need to follow the same patterns, as long as it abstracts them appropriately for users. This section primarily explores where the implementation can differ from the user's perspective."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Feature Matrices"
      },
      {
        "type": "paragraph",
        "text": "Because feature values are defined in result sets from individual experiments and the definition of a feature can change depending on context, it typically isn't worth the effort to try and put these all into a single table or data structure at once. Instead, it's usually best to store result sets as flat files, in a format that makes it easy to extract feature values into slices. (This may not even mean storing them with feature columns.)"
      },
      {
        "type": "paragraph",
        "text": "Since the goal is to allow maximum flexibility for generating slices, this mostly means allowing maximum flexibility for these flat files (while maintaining consistency across each data category) but there are a few important considerations:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Candidate ids: Files should use consistent ids for candidates and where it makes sense, use a consistent column name for these ids."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Comparable features: Features that might be considered the same or comparable across result sets should use the same vocabulary/ontology or units as much as possible."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Feature metadata: Enough metadata should be recorded about each feature in each result set to ensure that it can be properly interpreted in comparison to other result sets."
        ]
      },
      {
        "type": "paragraph",
        "text": "In particular, each dataset in the lineage should include detailed metadata about the processing step and input datasets that were used to generate it, so that each feature value can be traced back to its source."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Extracting Slices"
      },
      {
        "type": "paragraph",
        "text": "The implementation details of how and where you store this data will partially depend on the tools that your team plans on using to extract these section of the matrix."
      },
      {
        "type": "paragraph",
        "text": "In drug discovery teams that haven't carefully planned out how they'll use data, slices are mostly created manually by users in spreadsheets, which can introduce errors with significant downstream effects. The more you can give them tools that do the calculations for them, ideally with an audit trail, the more you can avoid these errors."
      },
      {
        "type": "paragraph",
        "text": "Check out the [Interfaces section](/frameworks/systems/workflows) of the \"Designing Data Infrastructure\" framework for more on what this can look like."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Not-quite features"
      },
      {
        "type": "paragraph",
        "text": "You'll often run across kinds of data that don't quite fit the model of features and feature values. For example, when you test different candidates at different sets of concentrations, do those count as different features or the same? How do you deal with measurements of combination therapies?"
      },
      {
        "type": "paragraph",
        "text": "It's typically fine to leave the data in a rawer form and figure out how to translate them into features (or some other form) when you know what you want your slice to look like. Or you could pre-calculate different forms of the features and decide later which ones to use. By this stage, the data is usually small enough that you won't even notice the storage and compute costs."
      },
      {
        "type": "paragraph",
        "text": "The point of this framework isn't to lock you into a restrictive way to format your data. It's to give you a general way of thinking about it so you can choose when to follow it and when to fudge it."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Structured and Unstructured Data"
      },
      {
        "type": "paragraph",
        "text": "Much of the information that needs to be stored in datasets will be significantly less structured than the nice, clean tables that databases are built for. This includes both the raw forms of many types of readouts and the metadata for readouts at every step, required to trace their lineage."
      },
      {
        "type": "paragraph",
        "text": "Because of this, it's typically best to keep data in folders in flat files in folders so that the metadata and unstructured data can live next to the more structured files. The structured data can then be copied/mirrored into more structured databases, but the source of truth should generally be the flat files where it's much harder to lose track of the metadata."
      }
    ]
  }
]

    export default data;
    